import { useEffect, useMemo, useRef } from 'react';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import type { ArcgisMapView } from './arcgisView';
import { syncExternalLayers } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/syncExternalLayers';
import { TN_DEMO_FORCE_DEFAULT_OFF_ITEM_ID_SET } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type { ArcGisAuthOptions } from '../arcgis-integration/externalLayerLoader';
import { getPortalUrl } from '../arcgis-integration/arcGisPortal';
import { useEnvironmentVariables } from 'utils/useEnvironmentVariables';
import { useDefaultLayerVisibilityById } from './useDefaultLayerVisibilityById';

type ExternalLayerSyncItem = Pick<
  ExternalLayer,
  | 'id'
  | 'arcgisItemId'
  | 'serviceUrl'
  | 'incident'
  | 'incidentId'
  | 'authType'
  | 'defaultVisible'
>;

/** Previously auto-seeded portal items that must not load via PortalItem. */
const SKIP_SYNC_ARCGIS_ITEM_IDS = new Set<string>([
  ...TN_DEMO_FORCE_DEFAULT_OFF_ITEM_ID_SET,
]);

function isRetiredAutoSeededArcGisLayer(
  layer: Pick<ExternalLayerSyncItem, 'arcgisItemId'>
): boolean {
  return (
    !!layer.arcgisItemId && SKIP_SYNC_ARCGIS_ITEM_IDS.has(layer.arcgisItemId)
  );
}

/**
 * Syncs external ArcGIS layers on the map with feature data from ArcGIS hosted
 * layers and manages their lifecycle.
 */
export function useExternalLayerSync({
  mapView,
  externalLayers,
  externalLayerVisibilityById,
  setExternalLayerVisibilityById,
  addedExternalLayersRef,
  setIsExternalLayersReady,
  setIsPublicExternalLayersReady,
  setIsProtectedExternalLayersReady,
  setExternalLayerLoadErrorById,
  incidentFilterId,
}: {
  mapView: ArcgisMapView | null;
  externalLayers: ExternalLayerSyncItem[];
  externalLayerVisibilityById: Map<string, boolean | null>;
  setExternalLayerVisibilityById: React.Dispatch<
    React.SetStateAction<Map<string, boolean | null>>
  >;
  addedExternalLayersRef: React.MutableRefObject<Map<string, __esri.Layer[]>>;
  setIsExternalLayersReady?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPublicExternalLayersReady?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsProtectedExternalLayersReady?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setExternalLayerLoadErrorById?: React.Dispatch<
    React.SetStateAction<Map<string, boolean>>
  >;
  incidentFilterId?: string | null;
}) {
  const { data: envData } = useEnvironmentVariables();
  const group = useCurrentGroup();
  const apiKey = envData?.ARCGIS_API_KEY;
  const portalUrl = getPortalUrl(group);
  const layersToSync = useMemo(
    () =>
      externalLayers.filter((layer) => !isRetiredAutoSeededArcGisLayer(layer)),
    [externalLayers]
  );
  const visibleLayerIdsKey = Array.from(externalLayerVisibilityById.entries())
    .filter(([, visible]) => visible)
    .map(([id]) => id)
    .sort()
    .join(',');

  // use ref here to get latest visibility state without re-running expensive effect
  const externalLayerVisibilityByIdRef = useRef(externalLayerVisibilityById);
  useEffect(() => {
    externalLayerVisibilityByIdRef.current = externalLayerVisibilityById;
  }, [externalLayerVisibilityById]);

  useDefaultLayerVisibilityById({
    layers: layersToSync,
    setVisibilityById: setExternalLayerVisibilityById,
    incidentFilterId,
  });

  // Cleanup: remove external layers when mapView changes or unmounts
  useEffect(() => {
    if (!mapView?.map) return;

    const map = mapView.map;
    const addedLayersById = addedExternalLayersRef.current;

    return () => {
      // Clean up errors for any layer that was currently managed/added
      const managedIds = Array.from(addedLayersById.keys());
      if (managedIds.length > 0) {
        setExternalLayerLoadErrorById?.((prev) => {
          const next = new Map(prev);
          let hasChanges = false;
          for (const layerId of managedIds) {
            hasChanges = next.delete(layerId) || hasChanges;
          }
          return hasChanges ? next : prev;
        });
      }

      // Remove actual layers from the map
      for (const layers of addedLayersById.values()) {
        for (const layer of layers) {
          map.remove(layer);
        }
      }
      addedLayersById.clear();
      setIsExternalLayersReady?.(false);
    };
  }, [
    mapView,
    addedExternalLayersRef,
    setExternalLayerLoadErrorById,
    setIsExternalLayersReady,
  ]);

  // Sync external layers to map
  useEffect(() => {
    if (!mapView?.map) {
      setIsExternalLayersReady?.(false);
      setIsPublicExternalLayersReady?.(false);
      setIsProtectedExternalLayersReady?.(false);
      return;
    }

    const map = mapView.map;
    let isCancelled = false;
    const hasPublicLayers = layersToSync.some(
      (l) => l.authType === 'apiKey' || l.authType === 'public'
    );
    const hasProtectedLayers = layersToSync.some(
      (l) => l.authType === 'userAuth'
    );

    const sync = async () => {
      setIsExternalLayersReady?.(false);
      setIsPublicExternalLayersReady?.(false);
      setIsProtectedExternalLayersReady?.(false);

      let missingPublicCredentials = false;
      let missingProtectedCredentials = false;

      const desiredExternalLayerIds = new Set(
        layersToSync.map((l) => String(l.id))
      );

      // Remove layers (and their errors) that no longer exist in the incoming layersToSync
      const addedLayersById = addedExternalLayersRef.current;
      const removedLayerIds: string[] = [];

      for (const [externalLayerId, layers] of addedLayersById) {
        if (!desiredExternalLayerIds.has(externalLayerId)) {
          for (const layer of layers) {
            map.remove(layer);
          }
          addedLayersById.delete(externalLayerId);
          removedLayerIds.push(externalLayerId);
        }
      }

      if (removedLayerIds.length > 0) {
        setExternalLayerLoadErrorById?.((prev) => {
          const next = new Map(prev);
          let hasChanges = false;
          for (const layerId of removedLayerIds) {
            hasChanges = next.delete(layerId) || hasChanges;
          }
          return hasChanges ? next : prev;
        });
      }

      // Add new layers
      for (const externalLayer of layersToSync) {
        const externalLayerId = String(externalLayer.id);
        const wantsVisible =
          externalLayerVisibilityByIdRef.current.get(externalLayerId) ?? false;
        // Hidden layers stay off the map so a nationwide feed is not loaded
        // until the user turns it on.
        if (!wantsVisible) continue;
        if (addedLayersById.has(externalLayerId)) continue;

        const authType = externalLayer.authType;
        const options: ArcGisAuthOptions | null =
          authType === 'public'
            ? { accessType: 'public' }
            : authType === 'apiKey'
              ? apiKey
                ? { accessType: 'apiKey', apiKey }
                : ((missingPublicCredentials = true), null)
              : authType === 'userAuth'
                ? portalUrl
                  ? { accessType: 'userAuth', portalUrl }
                  : ((missingProtectedCredentials = true), null)
                : null;

        if (!options) {
          setExternalLayerLoadErrorById?.((prev) => {
            const currentLoadError = prev.get(externalLayerId) ?? false;
            if (currentLoadError) {
              return prev;
            }

            const next = new Map(prev);
            next.set(externalLayerId, true);
            return next;
          });
          continue;
        }

        const { layers, loadError } = await syncExternalLayers(
          externalLayer,
          options
        );
        if (isCancelled) {
          return;
        }

        setExternalLayerLoadErrorById?.((prev) => {
          const currentLoadError = prev.get(externalLayerId) ?? false;
          if (currentLoadError === loadError) {
            return prev;
          }

          const next = new Map(prev);
          next.set(externalLayerId, loadError);
          return next;
        });

        if (layers.length > 0) {
          const isVisible =
            externalLayerVisibilityByIdRef.current.get(externalLayerId) ??
            false;
          for (const layer of layers) {
            clearScaleRange(layer);
            layer.visible = isVisible;
            map.add(layer);
          }
          addedLayersById.set(externalLayerId, layers);
        }
      }

      if (!isCancelled) {
        const isPublicReady =
          (hasPublicLayers && !missingPublicCredentials) || !hasPublicLayers;
        const isProtectedReady =
          (hasProtectedLayers && !missingProtectedCredentials) ||
          !hasProtectedLayers;

        setIsPublicExternalLayersReady?.(isPublicReady);
        setIsProtectedExternalLayersReady?.(isProtectedReady);
        setIsExternalLayersReady?.(isPublicReady && isProtectedReady);
      }
    };

    sync();
    return () => {
      isCancelled = true;
    };
    // Re-run when a layer is turned on so that layer is loaded. Already-added
    // layers are skipped, so this does not reload the catalog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layersToSync,
    visibleLayerIdsKey,
    mapView,
    apiKey,
    portalUrl,
    setIsPublicExternalLayersReady,
    setIsProtectedExternalLayersReady,
    setExternalLayerLoadErrorById,
    setIsExternalLayersReady,
  ]);
}

const clearScaleRange = (layer: __esri.Layer) => {
  if ('minScale' in layer) {
    (layer as __esri.Layer & { minScale: number }).minScale = 0;
  }
  if ('maxScale' in layer) {
    (layer as __esri.Layer & { maxScale: number }).maxScale = 0;
  }
};
